import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge } from '@/components/ui/Badge'
import { useProposal, useShareProposal, useProposalTracking, useRevokeLink } from '@/hooks/useProposals'
import { formatCurrency, formatDate } from '@/lib/utils'
import type { ProposalShareRequest, ProposalShareResponse } from '@/types'
import {
  Link, Send, Copy, Eye, Clock, CheckCircle2, XCircle, ExternalLink,
  MessageCircle, Mail, Shield, RefreshCw, ChevronDown, ChevronLeft,
  Loader2, QrCode, User, Phone, FileText, AlertTriangle,
} from 'lucide-react'

// ==========================================
// Status Config
// ==========================================

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  draft: { label: 'Rascunho', color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
  pending_approval: { label: 'Aguardando Aprovação', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300' },
  approved: { label: 'Aprovada', color: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' },
  rejected: { label: 'Rejeitada', color: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' },
  sent: { label: 'Enviada', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' },
  accepted: { label: 'Aceita', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300' },
  declined: { label: 'Recusada', color: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' },
  expired: { label: 'Expirada', color: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400' },
}

const LINK_STATUS_CONFIG: Record<string, { label: string; color: string; icon: typeof CheckCircle2 }> = {
  active: { label: 'Ativo', color: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300', icon: CheckCircle2 },
  expired: { label: 'Expirado', color: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400', icon: Clock },
  signed: { label: 'Assinado', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300', icon: Shield },
  revoked: { label: 'Revogado', color: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300', icon: XCircle },
}

const EXPIRES_OPTIONS = [
  { value: 7, label: '7 dias' },
  { value: 15, label: '15 dias' },
  { value: 30, label: '30 dias' },
  { value: 60, label: '60 dias' },
  { value: 90, label: '90 dias' },
]

// ==========================================
// Helpers
// ==========================================

function parseUserAgent(ua: string | null): string {
  if (!ua) return 'Desconhecido'
  if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome'
  if (ua.includes('Firefox')) return 'Firefox'
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari'
  if (ua.includes('Edg')) return 'Edge'
  if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera'
  return 'Outro'
}

function formatDuration(seconds: number | null): string {
  if (!seconds || seconds < 1) return '—'
  if (seconds < 60) return `${seconds}s`
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  if (m < 60) return `${m}min ${s}s`
  const h = Math.floor(m / 60)
  return `${h}h ${m % 60}min`
}

function formatDateTime(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
}

// ==========================================
// Main Page Component
// ==========================================

export default function ProposalSharePage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: proposal, isLoading: loadingProposal } = useProposal(id || null)
  const { data: trackingData, isLoading: loadingTracking } = useProposalTracking(id || null)
  const shareProposal = useShareProposal()
  const revokeLink = useRevokeLink()

  // Form state
  const [recipientName, setRecipientName] = useState('')
  const [recipientEmail, setRecipientEmail] = useState('')
  const [recipientPhone, setRecipientPhone] = useState('')
  const [message, setMessage] = useState('')
  const [expiresDays, setExpiresDays] = useState(30)
  const [sendEmail, setSendEmail] = useState(true)
  const [sendWhatsApp, setSendWhatsApp] = useState(false)

  // Result state
  const [shareResult, setShareResult] = useState<ProposalShareResponse | null>(null)
  const [copiedUrl, setCopiedUrl] = useState(false)

  // Expanded tracking rows
  const [expandedLinks, setExpandedLinks] = useState<Set<string>>(new Set())

  function toggleExpanded(linkId: string) {
    setExpandedLinks(prev => {
      const next = new Set(prev)
      if (next.has(linkId)) next.delete(linkId)
      else next.add(linkId)
      return next
    })
  }

  async function handleShare() {
    if (!id || !recipientName.trim()) return
    const data: ProposalShareRequest = {
      recipient_name: recipientName.trim(),
      recipient_email: recipientEmail.trim() || undefined,
      recipient_phone: recipientPhone.trim() || undefined,
      send_email: sendEmail,
      send_whatsapp: sendWhatsApp,
      message: message.trim() || undefined,
      expires_days: expiresDays,
    }
    shareProposal.mutate({ proposalId: id, data }, {
      onSuccess: (result) => {
        setShareResult(result)
        setRecipientName('')
        setRecipientEmail('')
        setRecipientPhone('')
        setMessage('')
      },
    })
  }

  async function handleCopyUrl(url: string) {
    try {
      await navigator.clipboard.writeText(url)
      setCopiedUrl(true)
      setTimeout(() => setCopiedUrl(false), 2000)
    } catch { /* ignore */ }
  }

  function handleRevoke(linkId: string) {
    if (!id) return
    if (confirm('Revogar este link? O destinatário não poderá mais acessar a proposta.')) {
      revokeLink.mutate({ proposalId: id, linkId })
    }
  }

  // Loading state
  if (loadingProposal) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!proposal) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-3" />
          <h2 className="text-lg font-semibold">Proposta não encontrada</h2>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/proposals')}>
            <ChevronLeft className="h-4 w-4 mr-1" /> Voltar
          </Button>
        </div>
      </div>
    )
  }

  const statusCfg = STATUS_CONFIG[proposal.status] || STATUS_CONFIG.draft
  const tracking = trackingData || []

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <Button variant="outline" size="sm" onClick={() => navigate('/proposals')}>
          <ChevronLeft className="h-4 w-4 mr-1" /> Propostas
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold truncate flex items-center gap-2">
            <Link className="h-5 w-5 text-primary flex-shrink-0" />
            Compartilhar Proposta
          </h1>
        </div>
      </div>

      {/* Proposal Info Bar */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">{proposal.opportunity_name || 'Proposta'}</span>
            <span className="text-xs text-muted-foreground">v{proposal.version}</span>
          </div>
          <div className="text-sm text-muted-foreground">{proposal.lead_company_name || '—'}</div>
          <div className="text-sm font-semibold text-green-600 dark:text-green-400">{formatCurrency(proposal.final_price)}</div>
          {proposal.recurring_monthly_total > 0 && (
            <div className="text-xs text-muted-foreground">{formatCurrency(proposal.recurring_monthly_total)}/mês</div>
          )}
          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusCfg.color}`}>
            {statusCfg.label}
          </span>
          <div className="text-xs text-muted-foreground ml-auto">Válida até {formatDate(proposal.valid_until)}</div>
        </div>
      </Card>

      {/* Two-column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* LEFT COLUMN — Share Panel */}
        <div className="space-y-4">
          <Card>
            <CardContent className="p-5 space-y-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Send className="h-5 w-5 text-primary" />
                Compartilhar Proposta
              </h2>

              {/* Recipient Name */}
              <div>
                <label className="text-sm font-medium mb-1 block">Nome do destinatário *</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input className="pl-9" placeholder="Nome completo" value={recipientName}
                    onChange={e => setRecipientName(e.target.value)} />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="text-sm font-medium mb-1 block">Email do destinatário</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input className="pl-9" type="email" placeholder="email@empresa.com" value={recipientEmail}
                    onChange={e => setRecipientEmail(e.target.value)} />
                </div>
              </div>

              {/* Phone */}
              <div>
                <label className="text-sm font-medium mb-1 block">WhatsApp (telefone)</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input className="pl-9" placeholder="+55 11 99999-9999" value={recipientPhone}
                    onChange={e => setRecipientPhone(e.target.value)} />
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="text-sm font-medium mb-1 block">Mensagem personalizada</label>
                <textarea
                  className="w-full p-3 border rounded-lg bg-background text-sm min-h-[80px] resize-y focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  placeholder="Olá, segue a proposta comercial para sua análise..."
                  value={message} onChange={e => setMessage(e.target.value)}
                />
              </div>

              {/* Expiry */}
              <div>
                <label className="text-sm font-medium mb-1 block">Validade do link</label>
                <select className="w-full px-3 py-2 rounded-md border bg-background text-sm"
                  value={expiresDays} onChange={e => setExpiresDays(Number(e.target.value))}>
                  {EXPIRES_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>

              {/* Checkboxes */}
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={sendEmail} onChange={e => setSendEmail(e.target.checked)}
                    className="rounded border-gray-300 text-primary focus:ring-primary" />
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">Enviar por Email</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={sendWhatsApp} onChange={e => setSendWhatsApp(e.target.checked)}
                    className="rounded border-gray-300 text-primary focus:ring-primary" />
                  <MessageCircle className="h-4 w-4 text-green-500" />
                  <span className="text-sm">Enviar por WhatsApp</span>
                </label>
              </div>

              {/* Submit */}
              <Button className="w-full" onClick={handleShare}
                disabled={!recipientName.trim() || shareProposal.isPending}>
                {shareProposal.isPending ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Gerando link...</>
                ) : (
                  <><Link className="h-4 w-4 mr-2" /> Gerar Link e Enviar</>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Share Result */}
          {shareResult && (
            <Card className="border-green-200 dark:border-green-800">
              <CardContent className="p-5 space-y-3">
                <h3 className="text-sm font-semibold flex items-center gap-2 text-green-700 dark:text-green-400">
                  <CheckCircle2 className="h-4 w-4" /> Link gerado com sucesso!
                </h3>

                {/* URL */}
                <div className="flex items-center gap-2">
                  <Input className="flex-1 text-xs font-mono" readOnly value={shareResult.public_url} />
                  <Button size="sm" variant="outline" onClick={() => handleCopyUrl(shareResult.public_url)}>
                    {copiedUrl ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  {shareResult.whatsapp_url && (
                    <a href={shareResult.whatsapp_url} target="_blank" rel="noopener noreferrer">
                      <Button size="sm" variant="outline" className="text-green-600">
                        <MessageCircle className="h-4 w-4 mr-1" /> Abrir WhatsApp
                      </Button>
                    </a>
                  )}
                  {shareResult.email_sent && (
                    <span className="inline-flex items-center text-xs text-green-600 gap-1">
                      <Mail className="h-3.5 w-3.5" /> Email enviado
                    </span>
                  )}
                  <a href={shareResult.public_url} target="_blank" rel="noopener noreferrer">
                    <Button size="sm" variant="outline">
                      <ExternalLink className="h-4 w-4 mr-1" /> Visualizar
                    </Button>
                  </a>
                </div>

                {/* QR Code placeholder */}
                <div className="flex items-center justify-center p-6 border rounded-lg border-dashed bg-muted/30">
                  <div className="text-center text-muted-foreground">
                    <QrCode className="h-10 w-10 mx-auto mb-2 opacity-40" />
                    <span className="text-xs">QR Code</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* RIGHT COLUMN — Tracking Dashboard */}
        <div className="space-y-4">
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Eye className="h-5 w-5 text-primary" />
                  Monitoramento em Tempo Real
                </h2>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
                  </span>
                  Auto-refresh 15s
                </div>
              </div>

              {loadingTracking ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : tracking.length === 0 ? (
                <div className="text-center py-12">
                  <Link className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">Nenhum link compartilhado ainda</p>
                  <p className="text-xs text-muted-foreground mt-1">Use o formulário ao lado para gerar o primeiro link.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {tracking.map((t) => {
                    const linkCfg = LINK_STATUS_CONFIG[t.link.status] || LINK_STATUS_CONFIG.active
                    const LinkIcon = linkCfg.icon
                    const isExpanded = expandedLinks.has(t.link.id)
                    const totalDuration = t.views.reduce((acc, v) => acc + (v.duration_seconds || 0), 0)

                    return (
                      <div key={t.link.id} className="border rounded-lg overflow-hidden">
                        {/* Link header */}
                        <div className="p-3 space-y-2">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className="font-medium text-sm truncate flex items-center gap-1.5">
                                <User className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                                {t.link.recipient_name || 'Sem nome'}
                              </div>
                              <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                                {t.link.recipient_email && (
                                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                                    <Mail className="h-3 w-3" /> {t.link.recipient_email}
                                  </span>
                                )}
                                {t.link.recipient_phone && (
                                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                                    <Phone className="h-3 w-3" /> {t.link.recipient_phone}
                                  </span>
                                )}
                              </div>
                            </div>
                            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold flex-shrink-0 ${linkCfg.color}`}>
                              <LinkIcon className="h-3 w-3" /> {linkCfg.label}
                            </span>
                          </div>

                          {/* Stats row */}
                          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Eye className="h-3.5 w-3.5" /> {t.link.view_count} {t.link.view_count === 1 ? 'visualização' : 'visualizações'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-3.5 w-3.5" /> {formatDuration(totalDuration)}
                            </span>
                            {t.link.last_viewed_at && (
                              <span>Último: {formatDateTime(t.link.last_viewed_at)}</span>
                            )}
                            {t.link.expires_at && (
                              <span>Expira: {formatDate(t.link.expires_at)}</span>
                            )}
                          </div>

                          {/* Signature */}
                          {t.link.is_signed && t.signature && (
                            <div className="flex items-center gap-2 p-2 rounded-md bg-emerald-50 dark:bg-emerald-950 border border-emerald-200 dark:border-emerald-800">
                              <Shield className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                              <div className="min-w-0">
                                <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-400">ASSINADO</span>
                                <span className="text-xs text-muted-foreground ml-2">
                                  {t.signature.signer_name}
                                  {t.signature.signer_email && ` (${t.signature.signer_email})`}
                                  {' — '}{formatDateTime(t.signature.signed_at)}
                                </span>
                              </div>
                            </div>
                          )}

                          {/* Actions */}
                          <div className="flex items-center gap-2">
                            <button onClick={() => toggleExpanded(t.link.id)}
                              className="flex items-center gap-1 text-xs text-primary hover:underline">
                              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                              {t.views.length} {t.views.length === 1 ? 'acesso' : 'acessos'}
                            </button>
                            {t.link.public_url && (
                              <button onClick={() => handleCopyUrl(t.link.public_url!)}
                                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
                                <Copy className="h-3 w-3" /> Copiar link
                              </button>
                            )}
                            {t.link.status === 'active' && (
                              <button onClick={() => handleRevoke(t.link.id)}
                                className="text-xs text-red-500 hover:text-red-600 flex items-center gap-1 ml-auto">
                                <XCircle className="h-3 w-3" /> Revogar
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Expanded view history */}
                        {isExpanded && t.views.length > 0 && (
                          <div className="border-t bg-muted/30">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-muted-foreground">
                                  <th className="text-left p-2 font-medium">Data/Hora</th>
                                  <th className="text-left p-2 font-medium">IP</th>
                                  <th className="text-left p-2 font-medium">Navegador</th>
                                  <th className="text-center p-2 font-medium">Duração</th>
                                  <th className="text-center p-2 font-medium">Páginas</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-border">
                                {t.views.map(v => (
                                  <tr key={v.id}>
                                    <td className="p-2">{formatDateTime(v.viewed_at)}</td>
                                    <td className="p-2 font-mono">{v.ip_address || '—'}</td>
                                    <td className="p-2">{parseUserAgent(v.user_agent)}</td>
                                    <td className="p-2 text-center">{formatDuration(v.duration_seconds)}</td>
                                    <td className="p-2 text-center">{v.pages_viewed || '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                        {isExpanded && t.views.length === 0 && (
                          <div className="border-t bg-muted/30 p-3 text-center text-xs text-muted-foreground">
                            Nenhuma visualização registrada ainda
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
